import React, { FC } from "react";
import { AxiosError } from "axios";
import { useMutation, useQueryClient } from "react-query";
import { Form, Formik } from "formik";
import { addDays, isAfter } from "date-fns";
import {
  Button,
  Popup,
  TextAreaFormik,
  ConfirmationPopup,
  CheckBoxFormik,
  CommonErrorPopup,
} from "component-library";
import { Trans, useTranslation } from "i18n";
import { useModal } from "shared-hooks";
import { authAxios as axios } from "api-generate-types/src/axios";
import { dataTestIds } from "data-testids";
import { HeapCustomEvent } from "shared-services";
import { selfTaperRequestQueryKey } from "pages/SelfTapeRequestPage/constants";
import { useTrackSelfTapeHeapEvent } from "shared/hooks/useTrackSelfTapeHeapEvent";
import { getTagminSelfTapeUrl } from "pages/SelfTapeRequestPage/SelfTapeRequestAgentParts/helpers";
import config from "config/global";
import { TagTapesBanner, NoTagminAgentBanner, TagminBanner } from "./Banners";

const AMOUNT_OF_DAYS = 30;

export const CONFIRM_EXPIRE_DATE_STORAGE_KEY =
  "CONFIRM_EXPIRE_DATE_STORAGE_KEY";

export const sendToClientButtonDataTestId =
  dataTestIds.nuJobsApp[
    "SelfTapeRequests.SendToClientModal.sendToClientButton"
  ];

interface Props {
  selfTapeRequestId: string;
  clientName: string;
  closeModal: () => void;
  isTagminUser?: boolean;
  isTagTapesUser?: boolean;
}

const SendToClientModal: FC<React.PropsWithChildren<Props>> = ({
  selfTapeRequestId,
  clientName,
  closeModal,
  isTagminUser,
  isTagTapesUser,
}) => {
  const queryClient = useQueryClient();
  const trackEvent = useTrackSelfTapeHeapEvent(selfTapeRequestId);
  const { t } = useTranslation();
  const { visible: confirmVisible, show: showConfirm } = useModal();

  const { mutateAsync, isLoading, error } = useMutation<
    unknown,
    AxiosError,
    string
  >({
    mutationFn: async (message: string) => {
      await axios.post(
        `${config.selfTapesApiUrl}/selftaperequest/${selfTapeRequestId}/sendtoclient`,
        {
          message,
        }
      );
    },
    onSuccess: async () => {
      trackEvent(HeapCustomEvent.AgentSendTapeRequestToPerformer);

      await queryClient.invalidateQueries({
        queryKey: [selfTaperRequestQueryKey],
      });
    },
  });

  const isNoTagminAgent = !isTagminUser && !isTagTapesUser;
  const expireDate = localStorage.getItem(CONFIRM_EXPIRE_DATE_STORAGE_KEY);
  const shouldShowConfirm =
    !expireDate ||
    isAfter(
      new Date(),
      new Date(localStorage.getItem(CONFIRM_EXPIRE_DATE_STORAGE_KEY) || "")
    );

  if (error)
    return (
      <CommonErrorPopup
        errorStatusCode={error?.response?.status}
        popupProps={{ close: closeModal }}
      />
    );

  return (
    <Formik
      initialValues={{
        hideConfirm: false,
        message: "",
      }}
      onSubmit={async (values) => {
        if (values.hideConfirm) {
          localStorage.setItem(
            CONFIRM_EXPIRE_DATE_STORAGE_KEY,
            addDays(new Date(), AMOUNT_OF_DAYS).toISOString()
          );
        }

        await mutateAsync(values.message);

        closeModal();
      }}
    >
      {({ submitForm }) => {
        if (confirmVisible)
          return (
            <ConfirmationPopup
              titleIcon="notice"
              texts={{
                title: t("common:confirmationPopUp.title.areYouSure"),
                proceed: t("common:selfTapes.request.action.sendToClient"),
                cancel: t("common:button.label.cancel"),
              }}
              handleProceed={submitForm}
              handleCancel={closeModal}
              loading={isLoading}
            >
              <Trans
                i18nKey="common:selfTapes.request.sendToClient.confirm.text"
                components={{
                  p: <p className="mb-4 break-word" />,
                  br: <br />,
                  a: (
                    <a
                      href={getTagminSelfTapeUrl(selfTapeRequestId)}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
              <CheckBoxFormik
                label={t(
                  "common:selfTapes.request.sendToClient.hideConfirm.label"
                )}
                name="hideConfirm"
                value
              />
            </ConfirmationPopup>
          );

        return (
          <Popup
            width={{ lg: 8, md: 8 }}
            close={closeModal}
            texts={{ closePopup: t("common:button.label.close") }}
            priority="high"
          >
            <div className="g-popup-content break-word">
              <div className="g-popup-content__header">
                {t("common:selfTapes.request.sendToClient.title", {
                  name: clientName,
                })}
              </div>
              <div className="g-popup-content__scrollable">
                <Form className="flex flex-col gap-5 p-2.5">
                  {isTagminUser && (
                    <TagminBanner selfTapeRequestId={selfTapeRequestId} />
                  )}
                  <p>{t("common:selfTapes.request.sendToClient.descr1")}</p>
                  <p>{t("common:selfTapes.request.sendToClient.descr2")}</p>
                  <TextAreaFormik
                    name="message"
                    label={t(
                      "common:selfTapes.request.sendToClient.message.label"
                    )}
                    annex={
                      <span className="text-s text-color-neutral-two">
                        ({t("common:label.optional")})
                      </span>
                    }
                    placeholder={t(
                      "common:selfTapes.request.sendToClient.message.placeholder"
                    )}
                    maxLength={1000}
                    rows={3}
                    inputWrapperClassName="mb-0"
                    showWordCountInsteadOfMinLength
                  />
                  {isTagminUser && !isTagTapesUser && <TagTapesBanner />}
                  {isNoTagminAgent && <NoTagminAgentBanner />}
                </Form>
                <hr className="g-split-line" />
                <div className="g-popup-content__buttons-set flex justify-between gap-2">
                  <Button
                    type="secondary"
                    text={t("common:button.label.close")}
                    onClick={closeModal}
                    className="w-auto"
                    size="medium"
                  />
                  <Button
                    type="primary"
                    text={t("common:selfTapes.request.action.sendToClient")}
                    onClick={shouldShowConfirm ? showConfirm : submitForm}
                    className="w-auto"
                    size="medium"
                    loading={isLoading}
                    data-testid={sendToClientButtonDataTestId}
                  />
                </div>
              </div>
            </div>
          </Popup>
        );
      }}
    </Formik>
  );
};

export default SendToClientModal;
